import _ from 'lodash'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Container } from './styled'

import { renderItem } from './TreeItem'
import { TreeState, CheckedMap, Data, CheckedObject } from './types'

const getMappedChildrenState = (treeState: TreeState, parentIdsList: string[], item: Data) => {
    let currentNode = treeState
    for (const parentId of parentIdsList) {
        currentNode = currentNode[parentId]?.children
    }

    currentNode[item.id] = { collapsed: false, checked: false, hasSubItemsChecked: false, children: {} }
    return treeState
}

interface Props {
    data: Data[]
    onChange?: (state: number[]) => void
    renderTitle?: (item: any, level: number) => ReactElement
    selectable: boolean
    filterTerm?: string
    filterProp?: keyof Data
    childrenPropName?: keyof Data & string
    onlyLayer?: number
    defaultValue?: any
}

const TreeSelector: React.FC<Props> = ({
    data,
    onChange,
    renderTitle,
    selectable,
    filterTerm,
    filterProp,
    onlyLayer,
    defaultValue,
    childrenPropName = 'children'
}) => {
    const [checkedMap, setCheckedMap] = useState<CheckedMap | null>(null)
    const [tree, setTree] = useState<TreeState | null>(null)
    const [viewItemsMap, setViewItemsMap] = useState<any>(null)

    const getInitialState = (
        data: Data[],
        childrenPropName: keyof Data,
        onlyLayer?: number,
        parentsIdsList: string[] = [],
        currentTreeState = {},
        level = 0
    ) => {
        let currentKeyMap = {}

        for (const item of data) {
            currentTreeState = getMappedChildrenState(currentTreeState, parentsIdsList, item)
            const children = item[childrenPropName]

            if (children) {
                const { keyMap } = getInitialState(
                    children as any,
                    childrenPropName,
                    onlyLayer,
                    [...parentsIdsList, item.id as string],
                    currentTreeState,
                    level + 1
                )

                currentKeyMap = {
                    ...currentKeyMap,
                    ...keyMap
                }
            }
            if (typeof onlyLayer === 'undefined' || onlyLayer === level) {
                currentKeyMap = {
                    ...currentKeyMap,
                    [item.id]: { ...item, checked: false }
                }

                const value = defaultValue[item.id]
                if (Object.values(defaultValue).length && value) {
                    setValueOnNewStates(currentTreeState, [...parentsIdsList, item.id], value, currentKeyMap)
                }
            }
        }
        return { keyMap: currentKeyMap, treeState: currentTreeState }
    }

    const setValueOnChildren = (currentNode: TreeState, value: boolean, newCheckedMap: CheckedMap, level: number) => {
        const childList = Object.entries(currentNode)

        for (const [key, nodeValue] of childList) {
            const viewKey = `${level}-${key}`

            if (!filterTerm || viewItemsMap[viewKey]) {
                const hasChildren = !!Object.keys(nodeValue.children).length
                currentNode[key].checked = !hasChildren && value
                currentNode[key].hasSubItemsChecked = !!Object.keys(nodeValue.children).length && value

                setValueOnChildren(nodeValue.children, value, newCheckedMap, level + 1)

                if (hasChildren) {
                    const { checkedCount, allChildrenCount } = hasAllSubItemsChecked(nodeValue.children)

                    currentNode[key].checked = allChildrenCount === checkedCount
                }

                if (key in newCheckedMap) newCheckedMap[key].checked = value
            }
        }
    }

    const hasAllSubItemsChecked = (currentNode: TreeState) => {
        const childList = Object.values(currentNode)
        let checkedChildrenCount = 0
        let checkedChildrenSubitems = false
        for (const { checked, hasSubItemsChecked } of childList) {
            if (checked) {
                checkedChildrenCount++
            } else if (hasSubItemsChecked) {
                checkedChildrenSubitems = true
            }
        }
        return {
            checkedCount: checkedChildrenCount,
            allChildrenCount: childList.length,
            subItemsChecked: checkedChildrenSubitems
        }
    }

    const setValueOnNewStates = (
        treeState: TreeState,
        path: string[],
        value: boolean,
        newCheckedMap: CheckedMap,
        level = 0
    ) => {
        const id = level in path ? path[level] : null

        if (id) {
            let currentNode = treeState[id]
            if (path.length - 1 > level) {
                setValueOnNewStates(currentNode.children, path, value, newCheckedMap, level + 1)
                const { allChildrenCount, checkedCount, subItemsChecked } = hasAllSubItemsChecked(currentNode.children)
                currentNode.hasSubItemsChecked = !!checkedCount || subItemsChecked

                const checked = checkedCount === allChildrenCount
                currentNode.checked = checked
            } else {
                setValueOnChildren(currentNode.children, value, newCheckedMap, level + 1)
                const { checkedCount, allChildrenCount, subItemsChecked } = hasAllSubItemsChecked(currentNode.children)

                if (filterTerm) {
                    const condition = checkedCount === allChildrenCount
                    currentNode.checked = condition && value
                } else {
                    currentNode.checked = value
                }
                currentNode.hasSubItemsChecked = !!checkedCount || subItemsChecked
            }
            if (id in newCheckedMap) {
                newCheckedMap[id].checked = currentNode.checked
            }
        }
    }

    useMemo(() => {
        const { keyMap, treeState } = getInitialState(data, childrenPropName, onlyLayer)

        setCheckedMap(keyMap)
        setTree(treeState)
        setViewItemsMap({})

        return treeState
    }, [data, defaultValue])

    useEffect(() => {
        if (typeof filterTerm === 'string' && filterProp) {
            filter(filterTerm.toUpperCase(), filterProp, data)
        }
    }, [filterTerm])

    const onSelectItem = (id: string, value: boolean, parents: string[]) => {
        if (tree && checkedMap) {
            let newCheckedMap: CheckedMap = {
                ...checkedMap
            }
            let newTree: TreeState = {
                ...tree
            }

            setValueOnNewStates(newTree, [...parents, id], value, newCheckedMap)
            setTree(newTree)
            setCheckedMap(newCheckedMap)

            if (onChange) {
                let checkedIds = []
                for (const item of Object.values(newCheckedMap)) {
                    if (item.checked) {
                        checkedIds.push(item.id as number)
                    }
                }
                onChange(checkedIds)
            }
        }
    }

    const onCollapse = async (id: string, value: boolean, parents: string[]) => {
        if (tree) {
            let newTree: TreeState = {
                ...tree
            }
            let currentNode = newTree
            for (const parentId of parents) {
                currentNode = currentNode[parentId].children
            }
            currentNode[id].collapsed = value
            setTree(newTree)
        }
    }

    const hasFilterTermOnChildren = (
        filterTerm: string,
        key: keyof Data,
        data: Data[],
        treeView: any,
        level: number
    ) => {
        let match = false
        let newTree = treeView
        if (data) {
            for (const node of data) {
                const { match: hasChildrenMatched, treeView: treeView } = hasFilterTermOnChildren(filterTerm, key, node?.children, newTree, level + 1)
                const hasMatched = String(node[key])
                    .toUpperCase()
                    ?.includes(filterTerm)

                const condition = hasMatched || hasChildrenMatched

                if (condition) {
                    match = true
                }

                treeView[`${level}-${node.id}`] = condition
                newTree = treeView
            }
        }

        return { match, treeView }
    }

    const filter = (filterTerm: string, key: keyof Data, data: Data[]) => {
        if (filterTerm) {
            let level = 0
            let treeView: any = {
                ...viewItemsMap
            }
            //Root Node
            let newTree = treeView
            for (const node of data) {
                const { match: hasChildrenMatched, treeView: treeView } = hasFilterTermOnChildren(filterTerm, key, node?.children, newTree, level + 1)
                const hasMatched = String(node[key])
                    .toUpperCase()
                    ?.includes(filterTerm)
                
                const condition = hasMatched || hasChildrenMatched

                treeView[`${level}-${node.id}`] = condition
                newTree = treeView
            }
            setViewItemsMap(treeView)
        }
    }
    const treeView = filterTerm ? viewItemsMap : null
    return (
        <Container>
            {checkedMap && tree
                ? data.map((item: any) =>
                      renderItem(
                          item,
                          selectable,
                          checkedMap,
                          onSelectItem,
                          renderTitle,
                          onCollapse,
                          tree,
                          treeView,
                          `0-${item.id}`
                      )
                  )
                : null}
        </Container>
    )
}

export default React.memo(TreeSelector)
